import React, { Ref } from 'react';
import { Box, BoxRef, Icon, Typography } from '@drone-js/core';
import { getHistory, getWindowLocation } from 'core/window/windowService';
import { getItemFromSessionStorage, removeItemFromSessionStorage, setItemInSessionStorage } from 'core-framework/window/storage';
import { SessionStorageKey } from 'core-framework/constant/StorageKeys';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import {
    ActiviitesWebViewHeaderContainerSeenEventActivitiesFieldsV1,
    ActivitiesWebViewHeaderBackButtonClickEventActivitiesFieldsV1,
    ActivitiesWebViewHeaderCartButtonClickEventActivitiesFieldsV1,
    ActivitiesWebViewHeaderShareButtonClickEventActivitiesFieldsV1,
} from '@agoda/analytics-messages-definition-activities/events.split';
import {
    ActiviitesWebViewHeaderContainerSeenEventActivitiesManifestV1,
    ActivitiesWebViewHeaderBackButtonClickEventActivitiesManifestV1,
    ActivitiesWebViewHeaderCartButtonClickEventActivitiesManifestV1,
    ActivitiesWebViewHeaderShareButtonClickEventActivitiesManifestV1,
} from '@agoda/analytics-messages-definition-activities/manifest/events';
import { AGODA_APP_HOME, openCartPage, openPrevNavStack, openShareSheet } from 'store/slice/navigation';
import { AppFeaturesToggleEnum, WebViewQuickShareParameters } from 'core/type/BootstrapConfig.types';
import { LandingLinkProviderApi } from 'store/api/activitiesApi';
import { isDetailPage, isHomePage } from 'core/constant/analytics/AnalyticsPageType';
import { Cid } from 'core/constant/Cid';
import { logError } from 'core-framework/util/LogMessage';
import { useSelector } from 'react-redux';
import { selectConfigFromBootstrap } from 'store/slice/bootstrap';

const WebViewHeaderContainerStyle: React.CSSProperties = { display: 'flex', flexDirection: 'row', flex: '1 1 0' };
export const WebViewHeader: React.FC<{
    headerContainerRef: Ref<BoxRef>;
    backButtonOnClick: () => void;
    showSearchBar: boolean;
    showCartIcon: boolean;
    isPreserveBackStack: boolean;
    isRebrandCartToSavedEnabled: boolean;
}> = (props) => {
    const { headerContainerRef, backButtonOnClick, showSearchBar, showCartIcon, isPreserveBackStack, isRebrandCartToSavedEnabled } = props;
    const config = useSelector(selectConfigFromBootstrap);
    const isHome = isHomePage(config.pageConfig.pageTypeId) || false;
    const history = getHistory();
    const shouldDisplayShareButton = config.webViewFeatureToggles.includes(AppFeaturesToggleEnum.EnableActivitiesDetailShare) && isDetailPage(config.pageConfig.pageTypeId);
    const location = getWindowLocation();
    const [landingPath, setLandingPath] = React.useState<string | null>(null);

    React.useEffect(() => {
        if (isPreserveBackStack) {
            const storedLandingPath = getItemFromSessionStorage(SessionStorageKey.LANDING_PATH);
            if (!storedLandingPath) {
                setItemInSessionStorage(SessionStorageKey.LANDING_PATH, location.pathname);
                setLandingPath(location.pathname);
            } else {
                setLandingPath(storedLandingPath);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    const onBackButtonClick = () => {
        trackElementEvent<ActivitiesWebViewHeaderBackButtonClickEventActivitiesFieldsV1>(ActivitiesWebViewHeaderBackButtonClickEventActivitiesManifestV1.ids, {});
        if (isPreserveBackStack) {
            if (landingPath && landingPath !== location.pathname) {
                backButtonOnClick();
            } else {
                removeItemFromSessionStorage(SessionStorageKey.LANDING_PATH);
                openPrevNavStack();
            }
            return;
        }
        backButtonOnClick();
    };

    const cartOnClick = () => {
        openCartPage();
        trackElementEvent<ActivitiesWebViewHeaderCartButtonClickEventActivitiesFieldsV1>(ActivitiesWebViewHeaderCartButtonClickEventActivitiesManifestV1.ids, {});
    };

    const getIconId = () => (isRebrandCartToSavedEnabled ? 'line.symbol.heart' : 'line.symbol.cart');

    const onShareClick = async () => {
        try {
            const url = new URL(location.href);
            url.searchParams.set('cid', Cid.ShareActivityCid);
            const shortenUrl = await LandingLinkProviderApi.generateShortenUrl(url.toString());

            if (shortenUrl && shortenUrl !== '') {
                const params: WebViewQuickShareParameters = {
                    title: config.cms[371248],
                    url: shortenUrl,
                    message: config.cms[371247],
                };
                openShareSheet(params);
                trackElementEvent<ActivitiesWebViewHeaderShareButtonClickEventActivitiesFieldsV1>(ActivitiesWebViewHeaderShareButtonClickEventActivitiesManifestV1.ids, {});
            } else {
                logError('[GenerateShortenUrl] Response does not contain shorten url', config.pageConfig.pageTypeId);
            }
        } catch (error) {
            logError('[GenerateShortenUrl] Unknown error occurred during URL shortening', config.pageConfig.pageTypeId);
        }
    };

    React.useEffect(() => {
        trackElementEvent<ActiviitesWebViewHeaderContainerSeenEventActivitiesFieldsV1>(ActiviitesWebViewHeaderContainerSeenEventActivitiesManifestV1.ids, {});
    }, []);

    return (
        <Box
            backgroundColor="base.surface"
            data-element-name="webview-header-container"
            data-testid="webview-header-container"
            display="flex"
            position={showSearchBar ? 'relative' : 'sticky'}
            top={showSearchBar ? undefined : '0'}
            zIndex={4}
            ref={headerContainerRef}
            height="61px"
            px="12"
        >
            <div style={WebViewHeaderContainerStyle}>
                {
                    // On desktop, empty tab already starts with 1 history entry. so when open a website, the history length becomes 2.
                    // In contrast, deep linking from app doesn't create history entry, so the history length will remain 1.
                    (history.length <= 1 || isHome) && !isPreserveBackStack ? (
                        // Need Anchor with hyperlink to DeepLink AGODA_APP_HOME here to support opening deeplink on WebKit engine
                        <Box
                            as="a"
                            href={AGODA_APP_HOME}
                            display="flex"
                            flex={1}
                            alignItems="center"
                            justifyContent="center"
                            data-element-name="mob-back-button"
                            data-testid="mob-back-button"
                        >
                            <Icon id="line.arrow.thin-back" size="20" />
                        </Box>
                    ) : (
                        <Box
                            as="button"
                            display="flex"
                            flex={1}
                            alignItems="center"
                            justifyContent="center"
                            onClick={onBackButtonClick}
                            data-element-name="mob-back-button"
                            data-testid="mob-back-button"
                        >
                            <Icon id="line.arrow.thin-back" size="20" />
                        </Box>
                    )
                }
                <Box justifyContent="center" flex={10} display="flex" alignItems="center">
                    <Typography variant="heading-default">{config.cms[127972]}</Typography>
                </Box>
                {shouldDisplayShareButton && (
                    <Box
                        as="button"
                        flex={1}
                        display="flex"
                        flexDirection="row-reverse"
                        alignItems="center"
                        justifyContent="center"
                        data-element-name="activities-web-view-header-share-button"
                        data-testid="mob-share-button"
                        onClick={onShareClick}
                    >
                        <Icon id="line.symbol.share" size="20" />
                    </Box>
                )}
                {showCartIcon ? (
                    <Box
                        as="button"
                        flex={1}
                        display="flex"
                        flexDirection="row-reverse"
                        alignItems="center"
                        justifyContent="center"
                        data-element-name="mob-cart-button"
                        data-testid="mob-cart-button"
                        onClick={cartOnClick}
                    >
                        <Icon id={getIconId()} size="20" />
                    </Box>
                ) : (
                    <Box flex={1} display="flex" flexDirection="row-reverse" alignItems="center" justifyContent="center" />
                )}
            </div>
        </Box>
    );
};
WebViewHeader.displayName = 'WebViewHeader';
